import type { BillingTier } from "schemas/generated/billing";

import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { z } from "zod";

export const billingTierSchema = z
  .enum(["free", "basic", "standard", "growth", "custom"])
  .describe("BillingTier");

export const BillingTiers: Record<Uppercase<BillingTier>, BillingTier> = {
  FREE: "free",
  BASIC: "basic",
  STANDARD: "standard",
  GROWTH: "growth",
  CUSTOM: "custom",
};

export const paymentProcessorSchema = z
  .enum(["shopify", "stripe", "none"])
  .describe("PaymentProcessor");

export const subscriptionStatusSchema = z
  .enum(["pending", "active", "canceled", "past_due"])
  .describe("SubscriptionStatus");

export const billingIntervalSchema = z
  .enum(["monthly", "yearly"])
  .describe("BillingInterval");

export const subscriptionDetailsSchema = z
  .object({
    id: z.string().uuid(),
    paymentProcessor: paymentProcessorSchema,
    paymentProcessorSubscriptionId: z.string().nullable(),
    name: billingTierSchema,
    startedAt: z.date(),
    endedAt: z.date().nullable(),
    monthlyAmount: z.number(),
    yearlyAmount: z.number().optional(),
    billingInterval: billingIntervalSchema,
    isCustom: z.boolean(),
  })
  .describe("SubscriptionDetails");

export const PlanDisplayName = {
  Free: "Free",
  Basic: "Basic",
  Standard: "Standard",
  Growth: "Growth",
  Custom: "Custom",
} as const;

export type PlanInfo = {
  tier: BillingTier;
  displayName: PlanDisplayName;
  monthlyPrice: number;
  yearlyPricePerMonth: number;
  href: string;
  cta: string;
  isMostPopular: boolean;
  next?: BillingTier;
  description: string;
  features: string[];
  maxPublishedPages: number | null;
  maxPublishedBlogPosts: number | null;
  maxPublishedSections: number | null;
  maxPublishedProductTemplates: number | null;
  maxPublishedElements: number | null;
  maxProjectMembers: number | null;
  maxShopifyIntegrations: number | null;
  aiCredits: number | null;
  maxExperiments: 0 | null;
  order: number | null;
  showOnPricingPlans: boolean;
  titleColor?: string;
  footerText?: string;
  imgSrc?: string;
  exampleBrandName?: string;
};

const GROWTH_PLAN_INFO: PlanInfo = {
  displayName: PlanDisplayName.Growth,
  monthlyPrice: 599,
  yearlyPricePerMonth: 499,
  tier: "growth",
  isMostPopular: false,
  href: "https://dashboard.replo.app/auth/signup?utm_source=pricing",
  cta: "Start Free",
  description: "Designed to perform for high-velocity teams.",
  features: [
    "Unlimited Published Items",
    "5 Shopify Stores",
    "Live Store Analytics",
    "50k A/B Test Sessions",
    "24/7 Support (Private Slack)",
  ],
  maxPublishedPages: 100,
  maxPublishedBlogPosts: 100,
  maxPublishedSections: 100,
  maxPublishedProductTemplates: 25,
  maxPublishedElements: null,
  maxProjectMembers: null,
  maxShopifyIntegrations: 5,
  aiCredits: null,
  maxExperiments: null,
  order: 4,
  showOnPricingPlans: true,
  titleColor: "text-black",
  footerText: "For globally-renowned brands like",
  imgSrc: "/images/customers/jiff.svg",
  exampleBrandName: "Jif",
};

const STANDARD_PLAN_INFO: PlanInfo = {
  next: "growth",
  monthlyPrice: 289,
  yearlyPricePerMonth: 249,
  displayName: PlanDisplayName.Standard,
  href: "https://dashboard.replo.app/auth/signup?utm_source=pricing",
  description: "Built for brands ready to scale and optimize their business.",
  cta: "Start Free",
  isMostPopular: true,
  tier: "standard",
  features: [
    "50 Published Items",
    "3 Shopify Stores",
    "Live Store Analytics",
    "25k A/B Test Sessions",
    "24/7 Support (Live Chat)",
  ],
  maxPublishedPages: 30,
  maxPublishedBlogPosts: 30,
  maxPublishedSections: 30,
  maxPublishedProductTemplates: 5,
  maxPublishedElements: 50,
  maxProjectMembers: null,
  maxShopifyIntegrations: 3,
  aiCredits: null,
  maxExperiments: null,
  order: 3,
  showOnPricingPlans: true,
  titleColor: "from-primary to-primary-muted",
  footerText: "For high-growth teams like",
  imgSrc: "/images/customers/jones-road-black.svg",
  exampleBrandName: "Jones Road",
};

const BASIC_PLAN_INFO: PlanInfo = {
  next: "standard",
  monthlyPrice: 119,
  yearlyPricePerMonth: 99,
  displayName: PlanDisplayName.Basic,
  tier: "basic",
  isMostPopular: false,
  href: "https://dashboard.replo.app/auth/signup?utm_source=pricing",
  cta: "Start Free",
  description: "Perfect for early-stage stores just getting started.",
  features: [
    "15 Published Items",
    "1 Shopify Store",
    "Live Store Analytics",
    "10k A/B Test Sessions",
    "24/7 Support (Email)",
  ],
  maxPublishedPages: 10,
  maxPublishedBlogPosts: 10,
  maxPublishedSections: 10,
  maxPublishedProductTemplates: 1,
  maxPublishedElements: 15,
  maxProjectMembers: null,
  maxShopifyIntegrations: 1,
  aiCredits: 50,
  maxExperiments: null,
  order: 2,
  showOnPricingPlans: true,
  titleColor: "from-primary to-primary-muted text-lime-500",
  footerText: "For up and coming brands like",
  imgSrc: "/images/customers/magic-mind.svg",
  exampleBrandName: "Magic Mind",
};

const FREE_PLAN_INFO: PlanInfo = {
  monthlyPrice: 0,
  yearlyPricePerMonth: 0,
  displayName: PlanDisplayName.Free,
  tier: "free",
  href: "https://dashboard.replo.app/auth/signup?utm_source=pricing",
  cta: "Start Free",
  isMostPopular: false,
  next: "basic",
  description: "Create Unlimited Drafts for as long as you need",
  features: ["Create Unlimited Drafts", "Email Support"],
  maxPublishedPages: 0,
  maxPublishedBlogPosts: 0,
  maxPublishedSections: 0,
  maxPublishedProductTemplates: 0,
  maxPublishedElements: 0,
  maxShopifyIntegrations: 1,
  aiCredits: 50,
  maxExperiments: 0,
  maxProjectMembers: 1,
  order: 1,
  showOnPricingPlans: false,
};

const CUSTOM_PLAN_INFO: PlanInfo = {
  monthlyPrice: 0,
  yearlyPricePerMonth: 0,
  displayName: PlanDisplayName.Custom,
  tier: "custom",
  cta: "Book Demo",
  isMostPopular: false,
  href: "https://dashboard.replo.app/auth/signup?utm_source=pricing",
  description:
    "Tailored to businesses or agencies that need higher limits and more flexibiity.",
  features: [
    "Everything in Growth",
    "More Shopify Stores",
    "More A/B Testing Sessions",
    "Product Roadmap Priority",
    "24/7 Premier Support",
  ],
  maxPublishedPages: null,
  maxPublishedBlogPosts: null,
  maxPublishedSections: null,
  maxPublishedProductTemplates: null,
  maxPublishedElements: null,
  maxProjectMembers: null,
  maxShopifyIntegrations: null,
  aiCredits: null,
  maxExperiments: null,
  order: null,
  showOnPricingPlans: false,
  titleColor: "text-black",
};

export type PlanDisplayName =
  (typeof PlanDisplayName)[keyof typeof PlanDisplayName];

export const getBillingPlanInfoByTier = (tier: BillingTier): PlanInfo => {
  return exhaustiveSwitch({ type: tier })({
    free: () => FREE_PLAN_INFO,
    basic: () => BASIC_PLAN_INFO,
    standard: () => STANDARD_PLAN_INFO,
    growth: () => GROWTH_PLAN_INFO,
    custom: () => CUSTOM_PLAN_INFO,
  });
};

export const BILLING_PLAN_CONFIRMATION_PARAM = "billing-confirmation";
export const BILLING_PLAN_CONFIRMATION_TYPE_PARAM = "confirmation-type";

// Reasons a workspace can't accommodate additional Shopify integrations:
export const shopifyIntegrationCapacityReasons = [
  // It is a shopify-billed workspace (i.e. its preferred payment provider is Shopify,
  // or it has an active billing plan on Shopify)
  "shopifyWorkspace",

  // It is at/over capacity for an additional integration
  "capacityExceeded",
] as const;

export const shopifyIntegrationCapacityReasonSchema = z
  .enum(shopifyIntegrationCapacityReasons)
  .describe("ShopifyIntegrationCapacityReason");

/**
 * Whether or not a given workspace can accommodate an additional Shopify Integration, and why not (if applicable)
 */
export const shopifyIntegrationCapacityStatusSchema = z
  .object({
    hasCapacity: z.literal(false),
    current: z.number(),
    maximum: z.number(),
    reason: shopifyIntegrationCapacityReasonSchema,
  })
  .or(z.object({ hasCapacity: z.literal(true) }))
  .describe("ShopifyIntegrationCapacityStatus");

export const getNonCustomBillingPlans = (): PlanInfo[] => {
  return ["free", "basic", "standard", "growth"].map((tier) =>
    getBillingPlanInfoByTier(tier as BillingTier),
  );
};
